import styled, { css } from 'styled-components';
import SharedLoader from '@exonet/react-shared/lib/Elements/Loader';
import { xs } from '@exonet/react-shared/lib/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * Get the prefix based on the properties.
 *
 * @param {boolean} alternate If true then the alternate vars are used.
 *
 * @returns {string} The btn prefix.
 */
const getPrefix = ({ alternate, primary }) => {
  if (alternate) return '--btn-alt';
  if (primary) return '--btn-primary';
  return '--btn';
};

/**
 * Get the tet color.
 *
 * @param {Object} options The options to get the css for.
 *
 * @returns {string} The text color.
 */
const elementColor = (options) => `${getPrefix(options)}-text`;

/**
 * Get the css for the button.
 *
 * @param {Object} options The options to get the css for.
 *
 * @returns {string} The colors for the button.
 */
const colors = (options) => {
  const prefix = getPrefix(options);

  return css`
    color: var(${prefix}-text);
    background-color: var(${prefix}-bg);
    border-color: var(${prefix}-bg);

    &:hover {
      background-color: var(${prefix}-bg-hover);
      border-color: var(${prefix}-bg-hover);
    }

    &[disabled] {
      color: var(${prefix}-text);
      background-color: var(${prefix}-bg);
      border-color: var(${prefix}-bg);
    }
  `;
};

export const Button = styled.button`
  display: flex;
  &,
  &:active,
  &:visited {
    flex-flow: row nowrap;
    align-items: center;
    cursor: pointer;
    border: var(--btn-border-width) solid transparent;
    border-radius: var(--btn-border-radius);
    padding: var(--btn-padding);
    font-family: var(--font-family-exo-2);
    line-height: var(--line-height);
    font-size: var(--btn-font-size);
    text-transform: uppercase;
    user-select: text;
    box-shadow: var(--btn-box-shadow);
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.65;
  }

  // Primary or alternate colors.
  ${colors}

  & + & {
    margin-left: var(--xs-padding-vertical);
  }

  ${({ size }) =>
    size === 'small' &&
    css`
      font-size: calc(var(--btn-font-size) * 0.7);
      padding: calc(var(--btn-padding-vertical) * 0.7) calc(var(--btn-padding-horizontal) * 0.7) !important;

      > svg {
        width: 14px;
        height: 14px;
      }
    `}

  ${({ size }) =>
    size === 'large' &&
    css`
      font-size: calc(var(--btn-font-size) * 1.3);
      padding: calc(var(--btn-padding-vertical) * 1.3) calc(var(--btn-padding-horizontal) * 1.3) !important;

      > svg {
        width: 18px;
        height: 18px;
      }
    `}
`;

export const Label = styled.span`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

export const Icon = styled(FontAwesomeIcon)`
  fill: var(${elementColor});
  color: var(${elementColor});
  width: 16px;
  height: 16px;

  & + ${Label} {
    margin-left: var(--xs-padding-horizontal);

    ${xs(css`
      margin-left: var(--xs-padding-horizontal);
    `)}
  }
`;

export const Loader = styled(SharedLoader).attrs({ mini: true })`
  > div {
    color: var(${elementColor});
  }

  & + ${Icon}, & + ${Label} {
    margin-left: var(--xs-padding-horizontal);

    ${xs(css`
      margin-left: var(--xs-padding-horizontal);
    `)}
  }
`;
